/**
 * The unified map's URL state (#327): `/map?mode=&latitude=&longitude=&zoom=`
 * plus `station` (observations) or, in forecast, `config` (a hand-picked
 * model) or `variable` (Auto, MR5d). The URL is the one source of truth for
 * the camera in both modes.
 */
import {
  type ForecastVariableKey,
  isForecastVariableKey,
} from './forecastCatalog';
import {parseForecastConfigRef} from '../ForecastMap/configRef';
import {DEFAULT_MAP_MODE, isMapMode} from './mapModes';
import type {MapMode} from './mapModes';

/**
 * Only `get` is read: React Native's URLSearchParams is incomplete, so a
 * caller can pass a router's search params or any object with `get`.
 */
export type UrlParamReader = {get(key: string): string | null};

export type MapUrlCamera = {latitude: number; longitude: number; zoom: number};

export type MapUrlState = {
  mode: MapMode;
  /**
   * Null unless latitude, longitude and zoom are all present and finite: a
   * partial camera (e.g. only `?zoom=8`) is dropped whole. The view a map
   * opens on fills a partial camera field by field: `initialMapCamera`.
   */
  camera: MapUrlCamera | null;
  /** Observations only: the station whose detail panel is open. */
  station: string | null;
  /**
   * Forecast only: the config the URL pins (a hand-picked model), as a
   * ref: the config's uuid, or a legacy link's integer id (core
   * `parseForecastConfigRef`, matched with `matchesForecastConfigRef`).
   */
  config: string | null;
  /**
   * Forecast only, and only without a config: the rail variable Auto draws
   * (#327 MR5d). Writing the config Auto happened to draw instead would pin
   * that model on reload.
   */
  variable: ForecastVariableKey | null;
};

export type MapUrlParam =
  | 'mode'
  | 'latitude'
  | 'longitude'
  | 'zoom'
  | 'station'
  | 'config'
  | 'variable';

// parseFloat, like /map today — but 0 is a real latitude, longitude or zoom,
// so finiteness decides, never truthiness.
const finite = (raw: string | null): number | null => {
  const value = raw === null ? NaN : parseFloat(raw);
  return Number.isFinite(value) ? value : null;
};

/**
 * Reads the map state from the URL. An unknown or absent `mode` is
 * observations. `station` and `config` are read only in their own mode, so
 * the state never carries a param its mode would not write back; `variable`
 * only in forecast and only without a config, which wins (a hand-picked
 * model brings its own variable). Anything but a rail variable reads null.
 */
export const parseMapUrlState = (reader: UrlParamReader): MapUrlState => {
  const rawMode = reader.get('mode');
  const mode = isMapMode(rawMode) ? rawMode : DEFAULT_MAP_MODE;
  const latitude = finite(reader.get('latitude'));
  const longitude = finite(reader.get('longitude'));
  const zoom = finite(reader.get('zoom'));
  const camera =
    latitude !== null && longitude !== null && zoom !== null
      ? {latitude, longitude, zoom}
      : null;
  const config =
    mode === 'forecast' ? parseForecastConfigRef(reader.get('config')) : null;
  const rawVariable = reader.get('variable');
  return {
    mode,
    camera,
    station: mode === 'observations' ? reader.get('station') || null : null,
    config,
    variable:
      mode === 'forecast' &&
      config === null &&
      isForecastVariableKey(rawVariable)
        ? rawVariable
        : null,
  };
};

/**
 * The params to write back, `null` meaning "delete the param". The default
 * mode is left out so `/map` stays the canonical observations URL; camera
 * numbers are written with `Number#toString`, as /map does today.
 */
export const serializeMapUrlState = (
  state: MapUrlState,
): Record<MapUrlParam, string | null> => ({
  mode: state.mode === DEFAULT_MAP_MODE ? null : state.mode,
  latitude: state.camera ? state.camera.latitude.toString() : null,
  longitude: state.camera ? state.camera.longitude.toString() : null,
  zoom: state.camera ? state.camera.zoom.toString() : null,
  station: state.mode === 'observations' ? state.station : null,
  config: state.mode === 'forecast' ? state.config : null,
  variable:
    state.mode === 'forecast' && state.config === null ? state.variable : null,
});

/**
 * The view `/map` opens on without a camera in the URL: the English Channel
 * at zoom 5, the centre and zoom today's /map has always hard-coded.
 */
export const DEFAULT_MAP_CAMERA: Readonly<MapUrlCamera> = {
  latitude: 49.736455137010665,
  longitude: -0.8569335937500001,
  zoom: 5,
};

/**
 * The camera a map opens on: each of latitude, longitude and zoom from the
 * URL when finite, otherwise from `fallback`, field by field, so `?zoom=8`
 * zooms the default centre (as /map always has). Unlike today's
 * `parseFloat(...) || default`, 0 is a value: the equator stays the equator.
 */
export const initialMapCamera = (
  reader: UrlParamReader,
  fallback: Readonly<MapUrlCamera>,
): MapUrlCamera => ({
  latitude: finite(reader.get('latitude')) ?? fallback.latitude,
  longitude: finite(reader.get('longitude')) ?? fallback.longitude,
  zoom: finite(reader.get('zoom')) ?? fallback.zoom,
});
